import React from "react";

import { Controller, useFormContext, useWatch } from "react-hook-form";

import { AgentVersionConfigurationInput } from "src/agents/types";
import { AgentModelInfo, Model } from "src/clients/agent-management";
import {
  DEPRECATED_BADGE_CONFIG,
  ModelOption,
  ModelSelectorDropdown,
  PROVIDER_BADGE_CONFIG,
} from "src/genai/ModelSelector";
import { BYOK_BADGE_CONFIG } from "src/genai/ModelSelector/ModelOptionBadge";

type AgentModelSelectorProps = {
  /** Whether the selector is disabled (e.g., for published versions) */
  disabled?: boolean;
  /** Available models adapted for the dropdown */
  modelOptions: ModelOption[];
};

/**
 * Build the compound model option ID used to highlight the selected model.
 * BYOK entries use "model_id:byok:credential_id", Taktile defaults use plain model_id.
 */
const buildSelectedOptionId = (
  model: string,
  credentialId: string | null | undefined,
): string => {
  if (credentialId) {
    return `${model}:byok:${credentialId}`;
  }
  return model;
};

/**
 * Parse a compound model option ID back into model and credential_id.
 * "gpt-5:byok:uuid" → { model: "gpt-5", credentialId: "uuid" }
 * "gpt-5" → { model: "gpt-5", credentialId: null }
 */
const parseSelectedOptionId = (
  optionId: string,
): { model: string; credentialId: string | null } => {
  const byokMarker = ":byok:";
  const idx = optionId.indexOf(byokMarker);
  if (idx !== -1) {
    return {
      model: optionId.slice(0, idx),
      credentialId: optionId.slice(idx + byokMarker.length),
    };
  }
  return { model: optionId, credentialId: null };
};

/**
 * Model selector component for agents.
 * Models are passed down from the parent component which fetches them.
 */
export const AgentModelSelector: React.FC<AgentModelSelectorProps> = ({
  disabled = false,
  modelOptions,
}) => {
  const { control, setValue } =
    useFormContext<AgentVersionConfigurationInput>();
  // Subscribe to credential_id changes so the component re-renders when
  // switching between shared-key and BYOK variants of the same model.
  // Using getValues() would only read the value once and miss updates
  // when the model name stays the same but the credential changes.
  const credentialId = useWatch({
    control,
    name: "model_config.credential_id",
  });

  return (
    <Controller
      control={control}
      name="model_config.model"
      render={({ field }) => {
        const selectedId = buildSelectedOptionId(field.value, credentialId);

        return (
          <ModelSelectorDropdown
            dataLoc="agent-model-selector"
            disabled={disabled}
            modelOptions={modelOptions}
            selectedModelId={selectedId}
            onSelect={(optionId) => {
              const parsed = parseSelectedOptionId(optionId);
              field.onChange(parsed.model as Model);
              setValue("model_config.credential_id", parsed.credentialId, {
                shouldDirty: true,
              });
            }}
          />
        );
      }}
    />
  );
};

export const convertAgentModelInfoToModelOption = (
  model: AgentModelInfo,
): ModelOption => ({
  id: buildSelectedOptionId(model.model_id, model.credential_id),
  displayName: model.display_name,
  description: model.description ?? undefined,
  provider: model.provider,
  disabled: false, // Agents API only returns available models
  disabledReason: undefined,
  routingTargetRegion: model.routing_target_region,
  badgeConfig: model.credential_id
    ? {
        ...BYOK_BADGE_CONFIG,
        label: model.credential_name ?? BYOK_BADGE_CONFIG.label,
      }
    : model.status === "deprecated"
      ? DEPRECATED_BADGE_CONFIG
      : PROVIDER_BADGE_CONFIG[model.provider],
});
